import loader from '../partials/loader'
import gtm from './gtm'
import toMinorUnits from '../tools/minorUnits'

const cartModel = (data) => ({
    ...loader,
    ...gtm,
    componentId: data.selfId,
    step: data.step,
    showCart: false,
    showJustAdded: false,
    justAddedProducts: [],
    showModalDiscontinued: false,
    orderId: data.orderId,
    cart: {
        products: [],
        showUpsellingAccuPro: false,
        accuProUpsellData: [],
    },
    discontinuedProducts: [],
    limitedStockProducts: [],
    hideCart() {
        window.Alpine.store('general').showOverlay = false
    },

    replaceUpselingPlaceholder(message, value) {
        return message.replace('{{formatted_currency}}', `<span class="text-accu-orange">${window.helpers.formatCurrencySign(value)}</span>`)
    },

    loadCart(options = {}) {
        return window.api.get(`/api/cart-summary?currency-id=${window.Alpine.store('currency').id}${this.orderId ? `&order-id=${this.orderId}` : ''}`)
            .then((response) => {
                this.cart = response.data
                this.cart.componentId = this.componentId

                this.updateComponentCount(response.data.products)
                this.cart.showUpsellingAccuPro = response.data.show_accupro_upsell
                this.cart.showUpsellingAccuProBuy = response.data.show_accupro_upsell_unsubscribed
                this.cart.accuProUpsellData = response.data.unavailable_accu_pro_options

                window.dispatchEvent(new CustomEvent('gtmdata', {
                    detail: {
                        gcp_cart_total: this.cart.order_total,
                        gcp_cart_no_products: window.Alpine.store('cart').productCount,
                        gcp_cart_no_lines: this.cart.products.length,
                    },
                }))

                this.cart.products.forEach((product, index) => {
                    this.cart.products[index].originalQty = this.cart.products[index].quantity
                })

                if (this.step > 0) {
                    window.dispatchEvent(new CustomEvent('gtmcheckoutsteps', {
                        detail: {
                            step: this.step,
                            options,
                            products: this.cart.products,
                            carrier: this.cart.carrier,
                        },
                    }))
                }

                window.dispatchEvent(new CustomEvent('cart-updated', { detail: this.cart }))
                this.stopLoader()

                this.sendCartRemarketing()

                // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#view_cart
                if (this.step === 0 || this.step === 1) {
                    // main cart loaded
                    const ga4event = this.getCheckoutEventBase()

                    // https:// developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#begin_checkout
                    if (this.step === 1) {
                        ga4event.event = 'begin_checkout'
                    }

                    window.dispatchEvent(new CustomEvent('gtmdata', {
                        detail: ga4event,
                    }))
                }

                window.Alpine.store('user').delivery_country_id = response.data.delivery ? response.data.delivery.id_country : null

                return this.cart
            })
            .catch((error) => {
                // The basket didn't load. On cart and checkout pages Tag Manager
                // holds the Google Ads remarketing hit until gtm_remarketing_cart
                // arrives, so send the page type on its own rather than no hit
                // at all. Callers still get the error.
                this.sendCartRemarketing(false)
                throw error
            })
    },

    sendCarrierCheckoutEvent(event) {
        // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#add_shipping_info
        const ga4event = this.getCheckoutEventBase()
        ga4event.event = 'add_shipping_info'
        ga4event.ecommerce.shipping_tier = `${event.option_name} / ${event.option_id}`
        window.dispatchEvent(new CustomEvent('gtmdata', {
            detail: ga4event,
        }))
    },

    sendPaymentCheckoutEvent(event) {
        // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#add_payment_info
        const ga4event = this.getCheckoutEventBase()
        ga4event.event = 'add_payment_info'
        ga4event.ecommerce.payment_type = event.payment_type
        window.dispatchEvent(new CustomEvent('gtmdata', {
            detail: ga4event,
        }))
    },

    // Google Ads remarketing on the cart and checkout pages. The basket isn't in
    // the page's server-side data, so partials/scripts.tpl can only say
    // ecomm_pagetype "cart" and asks Tag Manager to wait for this event, which
    // adds the product ids and value once the cart has loaded. Several cart
    // components load on these pages, so only the first one sends it. If the
    // basket fails to load, the page type goes on its own (withBasket false),
    // and partials/scripts.tpl sends it on its own if nothing has gone after
    // 8 seconds.
    sendCartRemarketing(withBasket = true) {
        if (window.accuCartRemarketingSent || !window.accuRemarketingAfterCartLoads) {
            return
        }

        const googleRemarketing = { ecomm_pagetype: 'cart' }

        if (withBasket) {
            // References exactly as stored, as on the product and order
            // pages: they are the Merchant Center item ids
            googleRemarketing.ecomm_prodid = this.cart.products.map((product) => String(product.reference))
            // Products ex-VAT, the basis every other payload uses
            googleRemarketing.ecomm_totalvalue = Number(this.cart.order_total_no_shipping) || 0
        }

        // Marked as sent only once the payload is built: if building it
        // throws, loadCart()'s catch can still send the page type.
        window.accuCartRemarketingSent = true
        window.dispatchEvent(new CustomEvent('gtmdata', {
            detail: {
                event: 'gtm_remarketing_cart',
                google_remarketing: googleRemarketing,
            },
        }))
    },

    getCheckoutEventBase() {
        return {
            event: 'view_cart',
            ecommerce: {
                currency: window.Alpine.store('currency').iso_code,
                value: this.cart.order_total_no_shipping,
                // Whole pence or cents, for ad pixels that reject decimals (OpenAI)
                value_minor: toMinorUnits(this.cart.order_total_no_shipping),
                items: this.cart.products.map((product) => ({
                    item_name: product.name,
                    item_id: product.reference,
                    price: product.price,
                    price_minor: toMinorUnits(product.price),
                    quantity: parseInt(product.quantity, 10),
                })),
            },
        }
    },

    showUnpurchaseableProductsToCustomer() {
        window.Alpine.store('general').showOverlay = true
        this.showModalDiscontinued = true
    },

    closeDiscontinuedModal() {
        if (this.showCart === false) {
            window.Alpine.store('general').showOverlay = false
        }
        this.showModalDiscontinued = false
    },

    async removeDiscontinued() {
        const payload = { detail: { products: [] } }

        this.cart.unpurchaseable.forEach((product, cartProductIndex) => {
            payload.detail.products.push({
                productId: product.product_id, quantity: product.quantity > product.max_quantity && product.max_quantity >= product.minimal_quantity ? product.max_quantity : 0, additive: false, position: cartProductIndex,
            })
        })

        await this.updateProductsQuantity(payload)
        this.closeDiscontinuedModal()
    },

    updateCartData($event) {
        if ($event.detail.componentId !== this.componentId) {
            this.cart = $event.detail
            this.updateComponentCount($event.detail.products)
            this.stopLoader()
        }

        if (this.cart.unpurchaseable && 'unpurchaseable' in this.cart && this.cart.unpurchaseable.length > 0 && this.componentId === 'nav') {
            this.discontinuedProducts = this.cart.unpurchaseable.filter((product) => product.quantity <= product.max_quantity)
            this.limitedStockProducts = this.cart.unpurchaseable.filter((product) => product.quantity > product.max_quantity)
            this.showUnpurchaseableProductsToCustomer()
        }
    },

    emptyCart() {
        this.startLoader()
        // The same details as a line's own remove button (cart-product-line.tpl),
        // so Tag Manager gets a remove_from_cart for every line
        const payload = { detail: { products: [], fromPage: 'Cart Page', cartAction: 'remove' } }

        this.cart.products.forEach((product, cartProductIndex) => {
            payload.detail.products.push({
                productId: product.id_product,
                quantity: 0,
                additive: false,
                position: cartProductIndex,
                // Per line, as the lines are removed in one event
                quantityChange: product.quantity,
                productDetails: {
                    name: product.name,
                    reference: product.reference,
                    priceUnitExTax: product.price,
                    categoryId: product.id_category_default,
                },
            })
        })

        return this.updateProductsQuantity(payload)
    },

    updateProductsQuantity($event) {
        this.startLoader()
        // gtm additions
        $event.detail.products.forEach((product) => {
            window.dispatchEvent(new CustomEvent('gtmproductupdate', {
                detail: {
                    event: $event,
                    product,
                },
            }))
        })

        return window.api.post('/api/cart/product', { products: $event.detail.products, currency_id: window.Alpine.store('currency').id })
            .then((response) => {
                this.justAddedProducts = response.data.products_added
                if (Object.values(this.justAddedProducts).length > 0) {
                    window.dispatchEvent(new CustomEvent('cart-products-added', { detail: this.justAddedProducts }))
                }

                if (!$event.detail.blockOverlay && Object.values(this.justAddedProducts).length > 0) {
                    this.showJustAdded = true
                    this.$store.general.showOverlay = true
                }

                this.loadCart()

                window.dispatchEvent(new CustomEvent('stop-loading'))
                const errorData = Array.isArray(response?.data?.errors) ? response.data.errors : []
                errorData.forEach((errorMessage) => {
                    console.log('errorMessage', errorMessage)
                    window.dispatchEvent(new CustomEvent('create-toast', {
                        detail: errorMessage,
                    }))
                })
            }).catch((error) => {
                window.dispatchEvent(new CustomEvent('stop-loading'))

                // The page is reloading to pass a Cloudflare check (see api.js)
                if (window.accuReloadingForChallenge) {
                    return
                }

                // A failed request can come back with anything, even an HTML page,
                // so only a list of messages from our API is shown as it is. Any
                // other failed request still gets a message, rather than the
                // click silently doing nothing.
                const errorData = Array.isArray(error?.response?.data?.errors) ? error.response.data.errors : []
                const messages = (errorData.length === 0 && error?.isAxiosError)
                    ? ['Your basket wasn\'t updated. Please try again.']
                    : errorData
                messages.forEach((errorMessage) => {
                    window.dispatchEvent(new CustomEvent('create-toast', {
                        detail: {
                            title: 'We encountered a problem...',
                            message: errorMessage,
                            type: 'info',
                        },
                    }))
                })
            })
    },

    updateCartCarrier(carrierId) {
        this.startLoader()
        const params = new URLSearchParams()
        params.append('carrier_id', carrierId)

        return window.api.post('/api/order/carrier', params).then((response) => {
            this.loadCart({ action: 'carrier_selection' })
        })
    },

    submitCartCarrierSelection() {
        if (this.step > 0) {
            window.dispatchEvent(new CustomEvent('gtmcheckoutsteps', {
                detail: {
                    step: this.step,
                    options,
                    products: this.cart.products,
                    carrier: this.cart.carrier,
                },
            }))
        }
    },

    updateComponentCount(products) {
        window.Alpine.store('cart').productCount = 0

        products.forEach((product) => {
            window.Alpine.store('cart').productCount += parseInt(product.quantity, 10)
        })
    },

    fireToast(title, message, type) {
        window.dispatchEvent(new CustomEvent('create-toast', { detail: { title, message, type } }))
    },

    init() {
        window.addEventListener('tax-toggled', (event) => {
            if (event.detail?.skipCartReload) {
                return
            }

            this.loadCart()
        })
    },
})
document.addEventListener('alpine:init', () => {
    window.Alpine.data('cartModel', cartModel)
})
export default cartModel
